import React, { useState, useEffect, useRef } from 'react';
import { Eye, Loader, Phone, X, Filter, FileText, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';
import { ordersApi, verifiedOrdersApi, employeesApi, productsApi, orderCampaignApi, fetchApiDirect, floorsApi } from '../services/api';
import { formatDate, maskPhone, formatCurrency } from '../utils/helpers';
import { useNavigate, useLocation } from 'react-router-dom';

import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import { Calendar, User, Tag, CreditCard, Layout, Users } from 'lucide-react';
import { message } from 'antd';
import { usePermissions } from '../hooks/Usepermissions';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import DateRangeFilter from '../components/DateRangeFilter';

// ─── Reason Modal ─────────────────────────────────────────────────────────────
const ReasonModal = ({ reasonModal, setReasonModal, onSubmit }) => {
  const textareaRef = useRef(null);
  const { currentTheme } = useTheme();

  useEffect(() => {
    if (reasonModal.isOpen && textareaRef.current) {
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
      textareaRef.current.focus();
    }
  }, [reasonModal.isOpen]);

  if (!reasonModal.isOpen) return null;

  const getHeaderColor = () => {
    switch (reasonModal.actionType) {
      case 'REJECT':  return 'text-red-600 border-red-100 bg-red-50';
      case 'HOLD':    return 'text-gray-700 border-gray-100 bg-gray-50';
      case 'PENDING': return 'text-orange-600 border-orange-100 bg-orange-50';
      default:        return 'text-gray-800 border-gray-100 bg-gray-50';
    }
  };

  const getButtonStyles = () => {
    switch (reasonModal.actionType) {
      case 'REJECT':  return { backgroundColor: '#ef4444' };
      case 'HOLD':    return { backgroundColor: '#6b7280' };
      case 'PENDING': return { backgroundColor: '#f97316' };
      default:        return { backgroundColor: currentTheme.color };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start pt-20 justify-center z-[9999] backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-2xl overflow-hidden">
        <div className={`px-6 py-4 border-b flex items-center justify-between ${getHeaderColor()}`}>
          <h3 className="text-lg font-bold flex items-center gap-2">{reasonModal.title}</h3>
          <button onClick={() => !reasonModal.isLoading && setReasonModal({ ...reasonModal, isOpen: false })} className="text-gray-400 hover:text-gray-700 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Reason <span className="text-red-500">*</span></label>
          <textarea
            ref={textareaRef}
            value={reasonModal.reason}
            onChange={(e) => setReasonModal({ ...reasonModal, reason: e.target.value })}
            placeholder="Please provide a valid reason here..."
            rows={2}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 outline-none resize-y text-gray-700 block"
          />
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button onClick={() => setReasonModal({ ...reasonModal, isOpen: false })} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors" disabled={reasonModal.isLoading}>Cancel</button>
          <button onClick={onSubmit} disabled={reasonModal.isLoading || !reasonModal.reason.trim()} className="px-6 py-2.5 text-white font-medium rounded-lg flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity" style={getButtonStyles()}>
            {reasonModal.isLoading ? <><Loader className="w-4 h-4 animate-spin" /> Processing...</> : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
function VerifiedOrders() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const { has, isAdmin }  = usePermissions();
  const navigate           = useNavigate();
  const location           = useLocation();

  const triggerDialer = (order) => {
    const lead = {
      id:            order.leadId || order.id,
      customerName:  order.customerName || order.lead?.customerName || '',
      contactNumber: order.contactNumber || order.lead?.contactNumber || '',
      campaign:      order.campaignName  || order.campaign           || '',
    };
    window.dispatchEvent(new CustomEvent('crm:dial', { detail: { lead } }));
  };

  const [orders,      setOrders]      = useState([]);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [loading,     setLoading]     = useState(true);
  const [totalItems,  setTotalItems]  = useState(0);
  const [tabCounts,   setTabCounts]   = useState({});
  const [currentPage, setCurrentPage] = useState(location.state?.currentPage || 1);
  const [activeTab,   setActiveTab]   = useState(location.state?.activeTab   || 'PROCESSING');
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDownloading,     setIsDownloading]     = useState(false);
  const itemsPerPage = 10;

  const getCurrentUser = () => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  };

  // ── Filter options — static ───────────────────────────────────────────────
  const [filterOptions, setFilterOptions] = useState({
    employees: [], teamLeads: [], categories: [], campaigns: [],
    floors: [],        // /floors API
    teams: [],         // floor select pe populate
    teamEmployees: [], // team select pe populate
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [teamsLoading,   setTeamsLoading]   = useState(false);
  const [empsLoading,    setEmpsLoading]    = useState(false);

  const emptyFilters = {
    datePreset: 'today', startDate: '', endDate: '',
    employeeId: '', employeeName: '',
    teamId: '', teamLeadName: '',
    floorId: '', floorName: '',
    category: '', paymentMode: '', campaignName: '', actionBy: '',
  };
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [tempFilters,   setTempFilters]   = useState(emptyFilters);

  const activeFiltersRef = useRef(emptyFilters);
  const activeTabRef     = useRef(activeTab);
  const searchRef        = useRef('');

  useEffect(() => { activeFiltersRef.current = activeFilters; }, [activeFilters]);
  useEffect(() => { activeTabRef.current     = activeTab;     }, [activeTab]);
  useEffect(() => { searchRef.current        = searchTerm;    }, [searchTerm]);

  const [reasonModal, setReasonModal] = useState({ isOpen: false, actionType: '', orderId: null, reason: '', isLoading: false, title: '' });

  const initialPageRef = useRef(location.state?.currentPage || null);
  const initialTabRef  = useRef(location.state?.activeTab   || null);

  useEffect(() => {
    const pg  = initialPageRef.current;
    const tab = initialTabRef.current;
    initialPageRef.current = null;
    initialTabRef.current  = null;
    if (pg && tab === activeTab) fetchOrders({ page: pg - 1 });
    else { fetchOrders({ page: 0 }); setCurrentPage(1); }
    fetchTabCounts();
    fetchFilterOptions();
  }, [activeTab]);

  useEffect(() => {
    if (!searchTerm) return;
    const t = setTimeout(() => { setCurrentPage(1); fetchOrders({ page: 0, overrideSearch: searchTerm }); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('.action-menu')) setOpenActionMenuId(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (isFilterModalOpen) setTempFilters(activeFilters); }, [isFilterModalOpen]);

  const currentPageRef = useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  useEffect(() => {
    const user = getCurrentUser();
    if (user?.role === 'ADMIN') return;
    const ordersRefreshTimer = setInterval(() => {
      const params = new URLSearchParams();
      params.append('status', 'PROCESSING');
      params.append('employeeId', user?.id);
      params.append('page', currentPageRef.current - 1);
      params.append('size', 10);
      fetchApiDirect(`/orders?${params.toString()}`)
        .then(data => {
          if (activeTabRef.current !== 'PROCESSING') return;
          const list  = Array.isArray(data) ? data : Array.isArray(data?.orders) ? data.orders : Array.isArray(data?.content) ? data.content : Array.isArray(data?.data) ? data.data : [];
          const total = data?.totalCount ?? data?.totalElements ?? data?.total ?? list.length;
          setOrders(list);
          setTotalItems(total);
        })
        .catch(err => console.error('Auto-refresh failed:', err));
    }, 60 * 1000);
    return () => clearInterval(ordersRefreshTimer);
  }, []);

  const resolveDatePreset = (preset, customStart = '', customEnd = '') => {
    const now = new Date();
    const fmt  = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtS = (d) => { d.setHours(0, 0, 0, 0); return fmt(d); };
    const fmtE = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };
    switch (preset) {
      case 'today':      return { startDate: fmtS(new Date(now)), endDate: fmtE(new Date(now)) };
      case 'yesterday':  { const y = new Date(now); y.setDate(y.getDate() - 1); return { startDate: fmtS(y), endDate: fmtE(new Date(y)) }; }
      case 'this_week':  { const d = now.getDay(), diff = now.getDate() - d + (d === 0 ? -6 : 1); const s = new Date(now); s.setDate(diff); return { startDate: fmtS(s), endDate: fmtE(new Date(now)) }; }
      case 'last_week':  { const s = new Date(now); s.setDate(s.getDate() - 7); return { startDate: fmtS(s), endDate: fmtE(new Date(now)) }; }
      case 'this_month': return { startDate: fmtS(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: fmtE(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
      case 'last_month': return { startDate: fmtS(new Date(now.getFullYear(), now.getMonth() - 1, 1)), endDate: fmtE(new Date(now.getFullYear(), now.getMonth(), 0)) };
      case 'this_year':  return { startDate: fmtS(new Date(now.getFullYear(), 0, 1)), endDate: fmtE(new Date(now.getFullYear(), 11, 31)) };
      case 'custom':     return { startDate: customStart || '', endDate: customEnd || '' };
      default:           return { startDate: '', endDate: '' };
    }
  };

  const tabToStatus = (tab) => {
    switch (tab) {
      case 'PROCESSING': return 'PROCESSING';
      case 'VERIFIED':   return 'VERIFIED';
      case 'ON_HOLD':    return 'ON_HOLD';
      case 'REJECTED':   return 'REJECTED';
      case 'PENDING':    return 'APPROVAL_PENDING';
      default:           return tab;
    }
  };

  const fetchOrders = async ({ page = currentPage - 1, overrideFilters = undefined, overrideSearch = undefined } = {}) => {
    try {
      setLoading(true);
      const user    = getCurrentUser();
      const filters = overrideFilters !== undefined ? overrideFilters : activeFiltersRef.current;
      const search  = overrideSearch  !== undefined ? overrideSearch  : searchRef.current;
      const tab     = activeTabRef.current;
      const params  = new URLSearchParams();

      params.append('status', tabToStatus(tab));
      if (search?.trim()) params.append('searchTerm', search.trim());

      const adminUser = user?.role === 'ADMIN';
      if (!adminUser) {
        params.append('employeeId', user?.id);
      } else {
        if (filters.employeeId) params.append('employeeId', filters.employeeId);
        if (filters.floorId)    params.append('floorId',    filters.floorId);
        if (filters.teamId)     params.append('teamId',     filters.teamId);
      }

      if (filters.paymentMode)  params.append('paymentMode',  filters.paymentMode);
      if (filters.campaignName) params.append('campaignName', filters.campaignName);
      if (filters.category)     params.append('category',     filters.category);

      const resolved = filters.datePreset
        ? resolveDatePreset(filters.datePreset, filters.startDate, filters.endDate)
        : { startDate: filters.startDate || '', endDate: filters.endDate || '' };

      if (resolved.startDate) params.append('startDate', resolved.startDate);
      if (resolved.endDate)   params.append('endDate',   resolved.endDate);
      if (resolved.startDate || resolved.endDate) params.append('dateType', 'createdDate');

      params.append('page', page);
      params.append('size', itemsPerPage);

      const data  = await fetchApiDirect(`/orders?${params.toString()}`);
      const list  = Array.isArray(data) ? data : Array.isArray(data?.orders) ? data.orders : Array.isArray(data?.content) ? data.content : Array.isArray(data?.data) ? data.data : [];
      let total = data?.totalCount ?? data?.totalElements ?? data?.total ?? list.length;
      switch (tabToStatus(tab)) {
        case 'PROCESSING':       total = data?.processingCount || data?.processCount || list.length; break;
        case 'VERIFIED':         total = data?.verifiedCount || list.length; break;
        case 'ON_HOLD':          total = data?.holdCount || list.length; break;
        case 'REJECTED':         total = data?.rejectedCount || list.length; break;
        case 'APPROVAL_PENDING': total = data?.pendingCount || list.length; break;
        default: break;
      }

      setOrders(list);
      setTotalItems(total);
      setTabCounts({
        PROCESSING:       data?.processingCount || data?.processCount || 0,
        VERIFIED:         data?.verifiedCount || 0,
        ON_HOLD:          data?.holdCount     || 0,
        REJECTED:         data?.rejectedCount || 0,
        PENDING:          data?.pendingCount  || 0,
      });
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrders([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTabCounts = () => {};

  // ── fetchFilterOptions — floors + employees static load ───────────────────
  const fetchFilterOptions = async () => {
    setFilterOptionsLoading(true);
    try {
      const [empRes, catRes, campRes, floorsRes] = await Promise.allSettled([
        employeesApi.getAllEmployees(),
        productsApi.getAllCategories(),
        orderCampaignApi.getAllCampaigns(),
        fetchApiDirect('/floors'),
      ]);

      const empRaw  = empRes.status  === 'fulfilled' ? empRes.value  : [];
      const empArr  = Array.isArray(empRaw) ? empRaw : Array.isArray(empRaw?.data) ? empRaw.data : Array.isArray(empRaw?.content) ? empRaw.content : [];
      const employees  = empArr.map(e => ({ name: e.name || e.employeeName || e.fullName || '', id: String(e.id || ''), teamLeadName: e.teamLeadName || e.teamLead || '', teamId: e.teamId })).filter(e => e.name);
      const teamLeads  = [...new Set(employees.map(e => e.teamLeadName).filter(Boolean))].sort();

      const catRaw  = catRes.status  === 'fulfilled' ? catRes.value  : [];
      const catArr  = Array.isArray(catRaw) ? catRaw : Array.isArray(catRaw?.data) ? catRaw.data : [];
      const categories = [...new Set(catArr.map(c => typeof c === 'string' ? c : c?.categoryName || c?.name).filter(Boolean))].sort();

      const campRaw = campRes.status === 'fulfilled' ? campRes.value : [];
      const campArr = Array.isArray(campRaw) ? campRaw : Array.isArray(campRaw?.data) ? campRaw.data : Array.isArray(campRaw?.content) ? campRaw.content : [];
      const campaigns  = [...new Set(campArr.map(c => c?.campaignName || c?.name || c).filter(Boolean))].sort();

      const floorsRaw = floorsRes.status === 'fulfilled' ? floorsRes.value : [];
      const floorsArr = Array.isArray(floorsRaw) ? floorsRaw : Array.isArray(floorsRaw?.data) ? floorsRaw.data : Array.isArray(floorsRaw?.content) ? floorsRaw.content : [];
      const floors = floorsArr.map(f => ({
        id:   String(f.id || f.floorId || ''),
        name: f.floorName || f.name || `Floor ${f.id || ''}`,
      })).filter(f => f.id);

      setFilterOptions(prev => ({ ...prev, employees, teamLeads, categories, campaigns, floors, teams: [], teamEmployees: [] }));
    } catch (err) {
      console.error('fetchFilterOptions error:', err);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  // ── Floor select → Teams fetch ────────────────────────────────────────────
  const handleFloorChange = async (floorId, floorName) => {
    setTempFilters(prev => ({ ...prev, floorId, floorName, teamId: '', teamLeadName: '', employeeId: '', employeeName: '' }));
    setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));
    if (!floorId) return;

    setTeamsLoading(true);
    try {
      const res = await fetchApiDirect(`/employees/grouped?floorId=${floorId}`);
      const teamsRaw = Array.isArray(res?.teams) ? res.teams : Array.isArray(res) ? res : [];
      const teams = teamsRaw.map(t => ({
        id:       String(t.teamId || t.id || ''),
        teamName: t.teamName || t.name || '',
      })).filter(t => t.id && t.teamName);
      // All employees of floor
      const allEmpsOfFloor = teamsRaw.flatMap(t =>
        (Array.isArray(t.employees) ? t.employees : []).map(e => ({
          id:   String(e.id || e.empId || ''),
          name: e.name || e.empName || e.employeeName || '',
        }))
      ).filter(e => e.id && e.name);
      setFilterOptions(prev => ({ ...prev, teams, teamEmployees: allEmpsOfFloor }));
    } catch (err) {
      console.error('Teams fetch error:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  // ── Team select → Employees fetch ─────────────────────────────────────────
  const handleTeamChange = async (teamId, teamName, currentFloorId) => {
    setTempFilters(prev => ({ ...prev, teamId, teamLeadName: teamName, employeeId: '', employeeName: '' }));
    setFilterOptions(prev => ({ ...prev, teamEmployees: [] }));
    if (!teamId) {
      // Reset to floor employees
      if (currentFloorId) handleFloorChange(currentFloorId, tempFilters.floorName);
      return;
    }

    setEmpsLoading(true);
    try {
      const params = currentFloorId ? `floorId=${currentFloorId}&teamId=${teamId}` : `teamId=${teamId}`;
      const res = await fetchApiDirect(`/employees/grouped?${params}`);
      const empsRaw = Array.isArray(res?.employees) ? res.employees
        : Array.isArray(res?.teams?.[0]?.employees) ? res.teams[0].employees
        : Array.isArray(res) ? res : [];
      const teamEmployees = empsRaw.map(e => ({
        id:   String(e.id || e.empId || ''),
        name: e.name || e.empName || e.employeeName || '',
      })).filter(e => e.id && e.name);
      setFilterOptions(prev => ({ ...prev, teamEmployees }));
    } catch (err) {
      console.error('Employees fetch error:', err);
    } finally {
      setEmpsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    activeFiltersRef.current = tempFilters;
    setCurrentPage(1);
    setIsFilterModalOpen(false);
    fetchOrders({ page: 0, overrideFilters: tempFilters });
  };

  const handleResetFilters = () => {
    setTempFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    activeFiltersRef.current = emptyFilters;
    setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));
    setCurrentPage(1);
    setIsFilterModalOpen(false);
    fetchOrders({ page: 0, overrideFilters: emptyFilters });
  };

  const handleViewOrderDetails = (order) => {
    navigate(`/orders/amount-decrease/${order.id}`, {
      state: { currentPage, activeTab, from: location.pathname },
    });
  };

  const handleVerifyOrder = async (orderId) => {
    try {
      await ordersApi.OrderStatus(orderId, 'Verified');
      message.success('Order verified successfully!');
      activeTab === 'VERIFIED' ? fetchOrders() : setActiveTab('VERIFIED');
    } catch (err) { message.error('Failed to verify order'); }
  };

  const openReasonModal = (orderId, type, title) =>
    setReasonModal({ isOpen: true, actionType: type, orderId, reason: '', isLoading: false, title });

  const submitReasonAction = async () => {
    const { actionType, orderId, reason } = reasonModal;
    if (!reason.trim()) { message.warning('Please enter a reason.'); return; }
    setReasonModal(prev => ({ ...prev, isLoading: true }));
    try {
      const userStr  = localStorage.getItem('user');
      const userObj  = userStr ? JSON.parse(userStr) : null;
      const userName = userObj?.name || 'Verifier';
      if      (actionType === 'HOLD')    await ordersApi.holdOrder(orderId,   { reason, holdReason: reason, notes: reason, actionBy: userName });
      else if (actionType === 'REJECT')  await ordersApi.updateOrder(orderId, { status: 'REJECTED', reason, rejectReason: reason, rejectedReason: reason, notes: reason, actionBy: userName, rejectedBy: userName });
      else if (actionType === 'PENDING') await ordersApi.markAsPending(orderId, { reason, pendingReason: reason, notes: reason, actionBy: userName });
      const messages = { HOLD: 'Order put on hold.', REJECT: 'Order marked as rejected.', PENDING: 'Order marked as pending.' };
      message.success(messages[actionType]);
      setReasonModal({ isOpen: false, actionType: '', orderId: null, reason: '', isLoading: false, title: '' });
      const targetTab = actionType === 'HOLD' ? 'ON_HOLD' : actionType === 'REJECT' ? 'REJECTED' : 'PENDING';
      activeTab === targetTab ? fetchOrders() : setActiveTab(targetTab);
    } catch (err) {
      message.error(`Failed to ${actionType.toLowerCase()} order`);
      setReasonModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleHoldOrder    = (id) => openReasonModal(id, 'HOLD',    'Hold Order');
  const handleRejectOrder  = (id) => openReasonModal(id, 'REJECT',  'Reject Order');
  const handlePendingOrder = (id) => openReasonModal(id, 'PENDING', 'Mark as Pending');

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      const user    = getCurrentUser();
      const filters = activeFiltersRef.current;
      const search  = searchRef.current;
      const tab     = activeTabRef.current;

      const params = new URLSearchParams();
      params.append('status', tabToStatus(tab));
      if (search?.trim()) params.append('searchTerm', search.trim());
      const canSeeAll = user?.role === 'ADMIN' || has('VERIFIED_VIEW');
      if (!canSeeAll)              params.append('employeeId', user?.id);
      else if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.floorId)    params.append('floorId',    filters.floorId);
      if (filters.teamId)     params.append('teamId',     filters.teamId);
      if (filters.paymentMode)     params.append('paymentMode',  filters.paymentMode);
      if (filters.campaignName)    params.append('campaignName', filters.campaignName);
      if (filters.category)        params.append('category',     filters.category);
      const resolved = filters.datePreset
        ? resolveDatePreset(filters.datePreset, filters.startDate, filters.endDate)
        : { startDate: filters.startDate || '', endDate: filters.endDate || '' };
      if (resolved.startDate) params.append('startDate', resolved.startDate);
      if (resolved.endDate)   params.append('endDate',   resolved.endDate);
      params.append('page', 0); params.append('size', 1);

      const countData  = await fetchApiDirect(`/orders?${params.toString()}`);
      const totalCount = countData?.totalCount ?? countData?.totalElements ?? countData?.total ?? 0;
      if (!totalCount) { message.warning('No orders to export'); return; }

      params.set('page', 0); params.set('size', totalCount);
      const allData = await fetchApiDirect(`/orders?${params.toString()}`);
      const allOrders = Array.isArray(allData) ? allData : Array.isArray(allData?.orders) ? allData.orders : Array.isArray(allData?.content) ? allData.content : Array.isArray(allData?.data) ? allData.data : [];
      if (!allOrders.length) { message.warning('No orders to export'); return; }

      const excelData = allOrders.map(order => ({
        'Order ID':     order.orderId || order.id,
        'Date':         order.orderDate ? formatDate(order.orderDate) : 'N/A',
        'Customer Name':order.customerName || order.lead?.customerName || 'N/A',
        'Phone':        order.contactNumber || order.lead?.contactNumber || 'N/A',
        'Product':      getProductName(order),
        'Category':     order.category || 'N/A',
        'Amount':       Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0)),
        'Payment Mode': order.paymentMode || 'COD',
        'Order Type':   order.orderType || 'Fresh Order',
        'Order By':     order.createdByName || 'N/A',
        'Status':       activeTab,
        'Verified By':  order.verifiedByName || 'N/A',
        'Remarks':      order.remarks || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook  = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Verified Orders');
      XLSX.writeFile(workbook, `Verified_Orders_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success(`${allOrders.length} orders exported successfully!`);
    } catch (err) {
      console.error(err);
      message.error('Failed to download excel file.');
    } finally {
      setIsDownloading(false);
    }
  };

  const getProductName = (order) => {
    if (order.orderItems?.length > 0)
      return order.orderItems.length === 1 ? order.orderItems[0].productName : `${order.orderItems[0].productName} +${order.orderItems.length - 1}`;
    return order.product || order.productName || '-';
  };

  const getsubCategory = (order) => {
    if (order.orderItems?.length > 0)
      return order.orderItems.length === 1 ? order.orderItems[0].subCategory : `${order.orderItems[0].subCategory} +${order.orderItems.length - 1}`;
    return order.subCategory || '-';
  };

  const displayValue = (v) => (v === null || v === undefined || v === '' || v === 0) ? '-/-' : v;

  const getColumnHeader = () => {
    switch (activeTab) {
      case 'PROCESSING': return 'ACTION BY';
      case 'VERIFIED':   return 'VERIFIED BY';
      case 'ON_HOLD':    return 'HOLD BY';
      case 'REJECTED':   return 'REJECTED BY';
      default:           return 'ACTION BY';
    }
  };

  const handlePageChange = (p) => { setCurrentPage(p); fetchOrders({ page: p - 1 }); };

  const tabs = [
    { key: 'PROCESSING', label: 'Processing Order', count: tabCounts.PROCESSING || 0 },
    { key: 'VERIFIED',   label: 'Verified Order',   count: tabCounts.VERIFIED   || 0 },
    { key: 'ON_HOLD',    label: 'Hold Order',        count: tabCounts.ON_HOLD    || 0 },
    { key: 'REJECTED',   label: 'Reject Order',      count: tabCounts.REJECTED   || 0 },
    { key: 'PENDING',    label: 'Pending Order',     count: tabCounts.PENDING    || 0 },
  ];

  const hasAnyAction = has('VERIFIED_EDIT');

  const LoadingDot = () => (
    <span className="ml-1 inline-block w-3 h-3 border border-t-transparent rounded-full animate-spin align-middle" style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
  );

  // Employee list for Order By dropdown
  const orderByOptions = filterOptions.teamEmployees.length > 0
    ? filterOptions.teamEmployees
    : filterOptions.employees;

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="flex flex-col gap-6">
        <h2 className="text-2xl desktop:text-3xl font-bold text-gray-800">
          {activeTab === 'PROCESSING' ? 'Processing Orders' : activeTab === 'VERIFIED' ? 'Verified Orders' : activeTab === 'ON_HOLD' ? 'Hold Orders' : activeTab === 'REJECTED' ? 'Rejected Orders' : 'Pending Orders'}
        </h2>

        {/* Search + Filter + Export */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex gap-4 flex-wrap">
          <ExpandableSearchBar searchTerm={searchTerm} onSearchChange={(v) => setSearchTerm(v)} placeholder="Search by order id or customer name" />
          {has('VERIFIED_DOWNLOAD') && <ExcelExportButton onClick={handleDownloadExcel} isDownloading={isDownloading} />}
          {has('VERIFIED_VIEW') && (
            <button onClick={() => setIsFilterModalOpen(true)} className="px-4 py-3 desktop:px-6 desktop:py-4 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors flex items-center gap-2 desktop:text-lg">
              <Filter className="w-5 h-5 desktop:w-6 desktop:h-6" />
              <span className="font-medium">Filter</span>
              {Object.keys(activeFilters).some(key => activeFilters[key] !== emptyFilters[key]) && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTheme.color }} />}
            </button>
          )}
        </div>

        {/* Tabs + Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {tabs.map(tab => (
                <button key={tab.key} onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                  className="flex-shrink-0 px-6 py-4 desktop:px-8 desktop:py-5 border-b-2 font-medium text-sm desktop:text-base transition-colors whitespace-nowrap"
                  style={{ color: activeTab === tab.key ? currentTheme.color : '#6b7280', borderColor: activeTab === tab.key ? currentTheme.color : 'transparent' }}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="text-white" style={{ background: currentTheme.color }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">ORDER ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">DATE</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">NAME</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">PRODUCT</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">CATEGORY</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">AMOUNT</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">PAY MODE</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">ORDER TYPE</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">ORDER BY</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">{getColumnHeader()}</th>
                      {activeTab === 'REJECTED' && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">REJECT REMARK</th>}
                      {activeTab === 'ON_HOLD'  && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">HOLD REMARK</th>}
                      {activeTab === 'PENDING'  && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">PENDING REMARK</th>}
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">REMARKS</th>
                      {hasAnyAction && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">ACTION</th>}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                    {orders.length > 0 ? orders.map((order, idx) => (
                      <tr key={order.id} className={`transition-colors ${isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50'}`}>
                        <td className="px-4 py-4 text-sm font-medium whitespace-nowrap" style={{ color: isDark ? 'white' : '#111827' }}>{order.orderId || order.id}</td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{order.orderDate ? formatDate(order.orderDate) : '-'}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{order.customerName || order.lead?.customerName}</span>
                            <button onClick={() => triggerDialer(order)}
                              className={`flex items-center space-x-1 text-xs transition-colors group w-fit mt-0.5 ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-[#449978]'}`}
                              title={`Call ${order.customerName || order.lead?.customerName}`}>
                              <span className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${isDark ? 'bg-white/10 group-hover:bg-white/20' : 'bg-green-500 group-hover:bg-[#449978]'}`}>
                                <Phone className={`w-2.5 h-2.5 ${isDark ? 'text-gray-400' : 'text-white'}`} />
                              </span>
                              <span className="group-hover:underline">{maskPhone(order.contactNumber || order.lead?.contactNumber)}</span>
                            </button>
                          </div>
                        </td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{getProductName(order)}</td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{getsubCategory(order)}</td>
                        <td className={`px-4 py-4 text-sm font-semibold whitespace-nowrap ${isDark ? 'text-green-400' : 'text-green-600'}`}>{formatCurrency(Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0)))}</td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                          {order.paymentMode === 'ONLINE' ? 'Online' : order.paymentMode === 'CASH' || order.paymentMode === 'COD' ? 'COD' : order.paymentMode || 'COD'}
                        </td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{order.orderType || 'Fresh Order'}</td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}><p className="font-medium">{order.createdByName || '—'}</p></td>
                        <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                          {activeTab === 'PROCESSING' && <p className="font-semibold">{'—'}</p>}
                          {activeTab === 'VERIFIED'   && <p className="font-semibold">{order.verifiedByName || '—'}</p>}
                          {activeTab === 'REJECTED'   && <p className="font-semibold">{order.rejectedByName || '—'}</p>}
                          {activeTab === 'ON_HOLD'    && <p className="font-semibold">{order.holdByName     || '—'}</p>}
                          {activeTab === 'PENDING'    && <p className="font-semibold">{order.pendingByName  || '—'}</p>}
                        </td>
                        {activeTab === 'REJECTED' && <td className="px-4 py-4 text-sm"><span className="text-red-600 font-bold">{displayValue(order.rejectReason || order.reason || order.rejectedReason || order.notes || order.remarks)}</span></td>}
                        {activeTab === 'ON_HOLD'  && <td className="px-4 py-4 text-sm"><span className="text-gray-600 font-bold">{displayValue(order.holdReason || order.reason || order.notes || order.holdRemarks)}</span></td>}
                        {activeTab === 'PENDING'  && <td className="px-4 py-4 text-sm"><span className="text-orange-600 font-bold">{displayValue(order.pendingReason || order.reason || order.notes || order.pendingRemarks)}</span></td>}
                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-normal break-words min-w-[200px]">{displayValue(order.remarks)}</td>
                        {hasAnyAction && (
                          <td className="px-4 py-4 relative action-menu">
                            <div className="flex items-center space-x-2">
                              <button onClick={() => handleViewOrderDetails(order)} className="p-1.5 rounded-lg transition-colors bg-opacity-10 hover:bg-opacity-20" style={{ backgroundColor: currentTheme.color }} title="View Details">
                                <Eye className="w-5 h-5 text-white" />
                              </button>
                            </div>
                            {openActionMenuId === order.id && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-48 z-10">
                                <button onClick={() => { handleViewOrderDetails(order); setOpenActionMenuId(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"><span>📋</span><span>Order details</span></button>
                                <button onClick={() => { handleHoldOrder(order.id);    setOpenActionMenuId(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"><span>⏸</span><span>Hold Order</span></button>
                                <button onClick={() => { handleRejectOrder(order.id);  setOpenActionMenuId(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"><span>✕</span><span>Reject Order</span></button>
                                <button onClick={() => { handlePendingOrder(order.id); setOpenActionMenuId(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"><span>🕐</span><span>Mark as Pending</span></button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    )) : (
                      <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500">No orders found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={handlePageChange} />
            </>
          )}
        </div>

        {/* ── Filter Modal ── */}
        {isFilterModalOpen && (
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsFilterModalOpen(false);
            }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-xl flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
                <h2 className="text-lg font-bold text-white">Filter Orders</h2>
                <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors"><X className="w-5 h-5 text-white" /></button>
              </div>
              <div className="p-6 overflow-y-auto flex-grow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Date Range */}
                  <div className="col-span-1">
                    <DateRangeFilter
                      label="Date Range"
                      placeholder="All Time"
                      preset={tempFilters.datePreset}
                      startDate={tempFilters.startDate}
                      endDate={tempFilters.endDate}
                      onChange={({ preset, startDate, endDate }) =>
                        setTempFilters({
                          ...tempFilters,
                          datePreset: preset,
                          startDate,
                          endDate,
                        })
                      }
                    />
                  </div>

                  {/* ── Floor (Admin only) ── */}
                  {isAdmin && (
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Floor {filterOptionsLoading && <LoadingDot />}
                      </label>
                      <ThemeSelect
                        value={tempFilters.floorName || ''}
                        onChange={(val) => {
                          const floor = filterOptions.floors.find(f => f.name === val);
                          handleFloorChange(floor?.id || '', floor?.name || '');
                        }}
                        options={[{ label: 'All Floors', value: '' }, ...filterOptions.floors.map(f => ({ label: f.name, value: f.name }))]}
                        icon={Users} className="w-full" placeholder="All Floors"
                      />
                    </div>
                  )}

                  {/* ── Team (Admin only, after floor select) ── */}
                  {isAdmin && (
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team {teamsLoading && <LoadingDot />}
                      </label>
                      <ThemeSelect
                        value={tempFilters.teamLeadName || ''}
                        onChange={(val) => {
                          const team = filterOptions.teams.find(t => t.teamName === val);
                          handleTeamChange(team?.id || '', team?.teamName || '', tempFilters.floorId);
                        }}
                        options={[
                          { label: teamsLoading ? 'Loading...' : 'All Teams', value: '' },
                          ...filterOptions.teams.map(t => ({ label: t.teamName, value: t.teamName }))
                        ]}
                        icon={Users} className="w-full" placeholder="All Teams"
                      />
                    </div>
                  )}

                  {/* ── Employee / Order By ── */}
                  {isAdmin && (
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agent {(filterOptionsLoading || empsLoading) && <LoadingDot />}
                      </label>
                      <ThemeSelect
                        value={tempFilters.employeeName}
                        onChange={(val) => {
                          const emp = orderByOptions.find(e => e.name === val);
                          setTempFilters(prev => ({ ...prev, employeeName: val, employeeId: emp?.id || '' }));
                        }}
                        options={[
                          { label: empsLoading ? 'Loading...' : 'All Agents', value: '' },
                          ...orderByOptions.map(e => ({ label: e.name, value: e.name }))
                        ]}
                        icon={User} className="w-full" placeholder="All Agents"
                      />
                    </div>
                  )}

                  {/* Category */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category {filterOptionsLoading && <LoadingDot />}</label>
                    <ThemeSelect value={tempFilters.category} onChange={(val) => setTempFilters({ ...tempFilters, category: val })} options={[{ label: 'All Categories', value: '' }, ...filterOptions.categories.map(c => ({ label: c, value: c }))]} icon={Tag} className="w-full" placeholder="All Categories" />
                  </div>

                  {/* Payment Mode */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                    <ThemeSelect value={tempFilters.paymentMode} onChange={(val) => setTempFilters({ ...tempFilters, paymentMode: val })}
                      options={[{ label: 'All Modes', value: '' }, { label: 'COD', value: 'COD' }, { label: 'Online', value: 'ONLINE' }, { label: 'UPI', value: 'UPI' }, { label: 'Card', value: 'CARD' }]}
                      icon={CreditCard} className="w-full" placeholder="All Modes" />
                  </div>

                  {/* Campaign */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campaign {filterOptionsLoading && <LoadingDot />}</label>
                    <ThemeSelect value={tempFilters.campaignName} onChange={(val) => setTempFilters({ ...tempFilters, campaignName: val })} options={[{ label: 'All Campaigns', value: '' }, ...filterOptions.campaigns.map(c => ({ label: c, value: c }))]} icon={Layout} className="w-full" placeholder="All Campaigns" />
                  </div>

                </div>
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 flex-shrink-0">
                <button onClick={handleResetFilters} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Reset</button>
                <button onClick={handleApplyFilters} className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm" style={{ backgroundColor: currentTheme.color }}>Apply Filters</button>
              </div>
            </div>
          </div>
        )}

        <ReasonModal reasonModal={reasonModal} setReasonModal={setReasonModal} onSubmit={submitReasonAction} />
      </div>
    </div>
  );
}

export default VerifiedOrders;